import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import {
  BoxProgrammingWeekCopyModal,
  type BoxProgrammingWeekCopyModalRef,
} from "@/modules/boxProgrammation/components/boxProgrammingHeader/boxProgrammingWeekCopyModal"
import { useBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { useBoxSlotDuplicateSession } from "@/modules/boxSlotSession/hooks/useBoxSlotSessionDuplicate"
import { useBoxSlotScheduledSessionsInRange } from "@/modules/boxSlotSession/hooks/useBoxSlotSessionScheduled"
import { useBoxSessionWeekCopy } from "@/modules/boxSlotSession/store/useBoxSessionWeekCopyStore"
import { cn } from "@/modules/lib/utils"
import { ImportTemplateIcon } from "@images/svgIcons/importTemplateIcon"
import { useProgrammationSessionImportSidebar } from "@modules/boxB2BProgram/hooks/useProgrammationSessionImportSidebar"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import dayjs from "dayjs"
import { FileInput, Files } from "lucide-react"
import { useMemo, useRef } from "react"

type BoxProgrammingHeaederActionsViewProps = {
  boxProgramId: string
  className?: string
} & React.ComponentProps<"div">

export function BoxProgrammingHeaederActionsView({
  boxProgramId,
  className,
  ...props
}: BoxProgrammingHeaederActionsViewProps) {
  const { selectedDate, getCalendarRange } = useCalendar()
  const { startDate, endDate } = getCalendarRange()
  const { data: boxProgrammation } = useBoxProgram(boxProgramId)
  const { data: scheduledSessions } = useBoxSlotScheduledSessionsInRange(
    boxProgramId,
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
  )

  const weekCopied = useBoxSessionWeekCopy()
  const { navigateToImportProgramSelection } = useProgrammationSessionImportSidebar()
  const { onCopyBoxProgrammationSessionWeek } = useBoxSlotDuplicateSession()

  const boxProgrammingWeekCopyModalRef = useRef<BoxProgrammingWeekCopyModalRef>(null)

  const canImportProgramming = useBoxStaffPermission(BoxStaffPermissions.IMPORT_PROGRAMMING)
  const canPasteWeek = useMemo(() => {
    if (weekCopied) {
      if (dayjs(selectedDate).weekday(6).isAfter()) {
        return (
          !dayjs(weekCopied.startDate).isSame(selectedDate, "week") || boxProgrammation.id !== weekCopied.boxProgramId
        )
      }
    }
    return false
  }, [weekCopied, boxProgrammation, selectedDate])

  const onCopyWeekClick = () => {
    if (scheduledSessions.length > 0) {
      onCopyBoxProgrammationSessionWeek(
        boxProgrammation.id,
        boxProgrammation.name,
        dayjs(startDate).toDate(),
        dayjs(endDate).toDate(),
      )
    } else {
      toast.warning(word("program.weekCopyPaste.copy.no_sessions_error"))
    }
  }

  const onPasteWeekClick = async () => {
    await boxProgrammingWeekCopyModalRef.current?.onPasteWeekClick()
  }

  const onImportButtonClick = () => {
    if (!canImportProgramming) {
      toast.error(word("box.programmation.activity.importTemplate_notAllowed"))
    } else if (startDate && endDate) {
      navigateToImportProgramSelection(boxProgrammation.id, dayjs(startDate).toDate(), dayjs(endDate).toDate())
    } else {
      toast.error(word("global.error.default"), { classNames: errorToastClassName() })
    }
  }

  return (
    <div className={cn("flex w-full flex-row items-center justify-between gap-2 pb-1", className)} {...props}>
      {/* Copy week button*/}
      <ActionButton
        action={onCopyWeekClick}
        icon={<Files color="currentColor" className={"text-textDefault-body"} />}
        buttonLablel={word("program.weekCopyPaste.copy.button")}
        disabled={false}
      />

      <ActionButton
        action={() => void onPasteWeekClick()}
        icon={
          <FileInput color="currentColor" className={canPasteWeek ? "text-textDefault-body" : "text-textDisabled"} />
        }
        buttonLablel={word("program.weekCopyPaste.paste.button")}
        disabled={!canPasteWeek}
      />

      {/* Import week button*/}
      <ActionButton
        action={onImportButtonClick}
        icon={<ImportTemplateIcon color="currentColor" className="text-textDefault-body" />}
        buttonLablel={word("box.programmation.activity.importTemplate")}
        disabled={false}
      />

      <BoxProgrammingWeekCopyModal boxProgramId={boxProgramId} ref={boxProgrammingWeekCopyModalRef} />
    </div>
  )
}

type ActionButtonProps = {
  action: () => void
  icon: React.JSX.Element
  buttonLablel: string
  disabled: boolean
  className?: string
} & React.ComponentProps<"div">
function ActionButton({ action, icon, buttonLablel, disabled, className, ...props }: ActionButtonProps) {
  return (
    <div className={cn("flex flex-col items-center justify-between gap-1 text-center", className)} {...props}>
      <Button
        variant="primary"
        size="iconMd"
        disabled={disabled}
        onClick={action}
        className={disabled ? "border-transparent bg-transparent" : ""}
      >
        {icon}
      </Button>

      <Text fontStyle="LS" className={disabled ? "text-textDisabled" : "text-textDefault-body"}>
        {buttonLablel}
      </Text>
    </div>
  )
}
